import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import type { AdPlacement } from "@/api/types";
import { env } from "@/lib/env";
import { sessionId } from "@/lib/session";

export interface AdContext {
  category?: string;
  q?: string;
  state?: number;
  limit?: number;
  exclude_store?: string;
}

export function useServedAds(placement: AdPlacement, ctx: AdContext = {}, enabled = true) {
  return useQuery({
    queryKey: ["ads", placement, ctx],
    queryFn: () => unwrap(api.GET("/api/v1/ads/serve/", { params: { query: { placement, ...ctx } } })),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export type AdEventType = "impression" | "view" | "q25" | "q50" | "q75" | "q100" | "click";

/* Events are batched and flushed every few seconds (or when the tab hides); clicks go at once. */
const queue: { token: string; type: AdEventType }[] = [];

const device = (): "mobile" | "desktop" =>
  typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px), (pointer: coarse)").matches ? "mobile" : "desktop";
let timer: number | undefined;

function flush(): void {
  window.clearTimeout(timer);
  timer = undefined;
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  const body = JSON.stringify({ events, device: device() });
  const url = `${env.apiBaseUrl}/ads/events/`;
  // keepalive lets the request finish while the page navigates away (like sendBeacon, but CORS-friendly).
  void fetch(url, {
    method: "POST",
    body,
    keepalive: true,
    headers: { "Content-Type": "application/json", "X-Session-ID": sessionId() },
  }).catch(() => undefined);
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush());
}

function enqueue(token: string, type: AdEventType): void {
  queue.push({ token, type });
  if (type === "click") return flush();
  timer ??= window.setTimeout(() => flush(), 3000);
}

export function trackImpression(token: string): void {
  if (seenTokens.has(token)) return;
  seenTokens.add(token);
  enqueue(token, "impression");
}

const seenTokens = new Set<string>();

export function trackClick(token: string): void {
  enqueue(token, "click");
}

const sentVideo = new Set<string>();

/** Video milestones (view = 2 s watched while visible; quartiles). Each is sent once per page view. */
export function trackVideo(token: string, type: Exclude<AdEventType, "impression" | "click">): void {
  const key = `${token}:${type}`;
  if (sentVideo.has(key)) return;
  sentVideo.add(key);
  enqueue(token, type);
}

/** Ref for an ad element: reports one impression once at least half of it has been visible for 1s. */
export function useImpression<T extends Element>(token: string | undefined) {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !token || typeof IntersectionObserver === "undefined") return;
    let seen: number | undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          seen = window.setTimeout(() => {
            enqueue(token, "impression");
            io.disconnect();
          }, 1000);
        } else {
          window.clearTimeout(seen);
        }
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      window.clearTimeout(seen);
      io.disconnect();
    };
  }, [token]);
  return ref;
}
